import { useMutation, useQueryClient } from '@tanstack/react-query';
import { feedApi } from '@/lib/api';
import { FeedPost, UserProfile } from '@/types';
import { Trash2, Plus, Calendar, MapPin, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

interface FeedsTabProps {
  posts: FeedPost[];
  onCreatePost: () => void;
  currentUser?: UserProfile;
}

export const FeedsTab = ({ posts, onCreatePost, currentUser }: FeedsTabProps) => {
  const queryClient = useQueryClient();

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => feedApi.deletePost(postId),
    onSuccess: () => {
      toast.success('Post deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete post');
    },
  });

  const handleDeletePost = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this post?')) {
      deletePostMutation.mutate(postId);
    }
  };

  // Check if current user can delete a post (only own posts)
  const canDeletePost = (post: FeedPost): boolean => {
    if (!currentUser) return false;
    // Use post.author.id since backend returns author object, not authorId field
    return post.author?.id === currentUser.id;
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Create Post Button - Enhanced Design */}
      <button 
        onClick={onCreatePost}
        className="w-full p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl border-2 border-dashed border-primary/20 hover:border-primary/40 hover:bg-gradient-to-r hover:from-primary/10 hover:to-accent/10 text-left transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <Plus className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-foreground">Share something...</p>
            <p className="text-sm text-muted-foreground">Post about events, rideshare, or items</p>
          </div>
        </div>
      </button>
      
      {posts.length === 0 ? (
        <div className="text-center py-16 bg-card/50 rounded-2xl border border-dashed border-muted">
          <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-lg font-medium text-muted-foreground">No posts yet</p>
          <p className="text-sm text-muted-foreground/60 mt-1">Be the first to share something with the community!</p>
        </div>
      ) : (
        posts.map((post) => (
          <div key={post.id} className="bg-card rounded-2xl p-5 border border-border/50 shadow-sm hover:shadow-md transition-all group relative">
            {/* Delete button - only visible to post author - always visible */}
            {canDeletePost(post) && (
              <button
                onClick={(e) => handleDeletePost(post.id, e)}
                className="absolute top-3 right-3 p-2 rounded-full bg-destructive/10 hover:bg-destructive text-destructive hover:text-white transition-all z-10"
                title="Delete your post"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            
            {/* Author Info */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-md">
                <span className="text-lg font-bold text-white">
                  {post.author.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{post.author.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(post.createdAt).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            
            {/* Post Type Badge */}
            <div className="mb-3">
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${
                post.type === 'party' 
                  ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-300' 
                  : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300'
              }`}>
                {post.type === 'party' ? '🎉' : '🔄'}
                {post.type === 'party' ? 'Party' : 'Trade'}
              </span>
            </div>

            {/* Post Content */}
            <div className="space-y-2">
              <h3 className="font-bold text-lg text-foreground">{post.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{post.description}</p>
            </div>

            {/* Post Details */}
            <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-border/30">
              {post.location && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <MapPin className="w-3 h-3 text-primary" />
                  <span>{post.location}</span>
                </div>
              )}
              {post.date && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <Calendar className="w-3 h-3 text-primary" />
                  <span>{post.date}</span>
                </div>
              )}
              {post.price !== undefined && post.price > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                  <DollarSign className="w-3 h-3 text-primary" />
                  <span>${post.price}</span>
                </div>
              )}
            </div>

            {/* Images */}
            {post.images && post.images.length > 0 && (
              <div className={`grid gap-2 mt-4 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {post.images.map((img, idx) => (
                  <img 
                    key={idx} 
                    src={img} 
                    alt="" 
                    className="rounded-xl w-full h-40 object-cover border border-border/30" 
                  />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default FeedsTab;
